import { ActivityEditorComponent } from './../../workflow/activities/activity-editor/activity-editor.component';
import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  TemplateRef,
  HostListener,
  ContentChild,
  AfterViewInit
} from '@angular/core';
import {
  NgbModal,
  ModalDismissReasons,
  NgbModalRef
} from '@ng-bootstrap/ng-bootstrap';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';

export enum ButtonStyle {
  Button,
  Link
}

export enum JanusModalType {
  DEFAULT = 'default',
  ERROR = 'error',
  SUCCESS = 'success',
  INFO = 'info'
}

@Component({
    selector: 'wm-modal-confirm',
    templateUrl: './modal-confirm.component.html',
    styleUrls: ['./modal-confirm.component.css'],
    standalone: false
})
export class ModalConfirmComponent implements OnInit, AfterViewInit {
  @Input() title: string;
  @Input() moduleId: string;
  @Input() commandText: string;
  @Input() cancelText = 'Cancel';
  @Input() options: { ariaLabelledBy: string };
  @Output() accepted: EventEmitter<any> = new EventEmitter<any>();
  @Output() canceled: EventEmitter<any> = new EventEmitter<any>();
  @Output() opened: EventEmitter<any> = new EventEmitter<any>();
  @Output() customButtonClicked: EventEmitter<any> = new EventEmitter<any>();
  @Input() enabled = true;
  @Input() showButtonText = false;
  @Input() buttonText: string;
  @Input() showButton = true;
  @Input() btnColor = 'primary';
  @Input() btnIcon: string;
  @Input() form: UntypedFormGroup;
  @Input() windowClass: string;
  @Input() small = false;
  @Input() tooltip: string;
  @Input() buttonClass: string;
  @Input() buttonStyle: ButtonStyle = ButtonStyle.Button;
  @Input() closeOnAccept = true;
  @Input() showCommandButton = true;
  @Input() customButtonText: string;
  @Input() customButtonColor = 'danger';
  @Input() showCancelButton = true;
  @Input() iconStyle;
  @Input() buttonStyling;

  // New inputs for Janus styling
  @Input() shareModalDesign = false;
  @Input() janusStyling = false;
  @Input() janusModalType: JanusModalType = JanusModalType.DEFAULT;
  @Input() janusModalWidth = '412px';
  @Input() janusDisableBackdropClose = false;
  @Input() isJanusMobileview: boolean = false; // Code added by Sanchit on 11/05/2026

  processing = false;
  originalAcceptText: string;
  _isOpen = false;

  // Janus modal state
  janusModalOpen = false;

  modalRef: NgbModalRef;

  // Template for modal content
  modalContentTemplate: TemplateRef<any>;

  @ViewChild('content', { static: true })
  private contentTpl: TemplateRef<any>;

  @ViewChild('contentCapture', { static: true })
  private contentCaptureTpl: TemplateRef<any>;

  shouldShowButton() {
    return this.showButton && this.buttonStyle === ButtonStyle.Button;
  }

  shouldShowLink() {
    return this.showButton && this.buttonStyle === ButtonStyle.Link;
  }

  constructor(private modalService: NgbModal, fb: UntypedFormBuilder) {
    this.form = fb.group({});
  }

  private updateHeaderZIndex(zIndex: string) {
  const header = document.querySelector('.header-parent-container') as HTMLElement;
  if (header) {
    header.style.zIndex = zIndex;
  }
}

  ngOnInit() {
    if(!this.options) {
      this.options = { ariaLabelledBy: `${this.moduleId}_modallabel` };
    }
    else {
      Object.assign(this.options, { ariaLabelledBy: `${this.moduleId}_modallabel` });
    }

    if (!this.buttonText && !this.btnIcon) {
      this.buttonText = this.commandText;
    }

    if (!this.buttonClass) {
      this.buttonClass = this.showButtonText
        ? `btn btn-raised btn-${this.btnColor}`
        : `btn btn-raised btn-${this.btnColor} glyphicon glyphicon-remove`;
    }

    this.opened.subscribe(() => { this._isOpen = true; if(this.janusStyling) this.updateHeaderZIndex('1'); });
    this.canceled.subscribe(() => { this._isOpen = false; if(this.janusStyling) this.updateHeaderZIndex('99'); });
    this.accepted.subscribe(() => { this._isOpen = false;  if(this.janusStyling) this.updateHeaderZIndex('99'); });
    this.customButtonClicked.subscribe(() => { this._isOpen = false; if(this.janusStyling) this.updateHeaderZIndex('99'); });
  }

  ngAfterViewInit() {
    // Use the captured content template
    this.modalContentTemplate = this.contentCaptureTpl;
  }

  setProcessing() {
    const btnText = this.originalAcceptText || this.commandText;

    if (!this.originalAcceptText) {
      this.originalAcceptText = btnText;
    }

    this.commandText = btnText + '...';
    this.processing = true;
  }

  cancelProcessing() {
    if (this.originalAcceptText) {
      this.commandText = this.originalAcceptText;
    }
    this.processing = false;
  }

  cancel() {
    this.cancelProcessing();
    if (this.modalRef) {
      this.modalRef.close('cancel');
    }
  }

  accept() {
    if (this.closeOnAccept) {
      if (this.modalRef) {
        this.modalRef.close('save');
      }
    } else {
      this.setProcessing();
      this.accepted.emit();
    }
  }

  dismis(reason) {
    if (reason === 'save') {
      this.accepted.emit(this.moduleId);
    } else {
      this.canceled.emit();
    }
  }

  open(content?) {
    this.processing = false;

    if (this.janusStyling) {
      this.openJanusModal();
    } else {
      this.openBootstrapModal();
    }

    this.opened.emit(true);
    return false;
  }

  private openBootstrapModal() {
    this.modalRef = this.modalService.open(this.contentTpl, {
      ariaLabelledBy: `${this.moduleId}_modallabel`,
      size: this.small ? 'sm' : 'lg',
      windowClass: this.windowClass || ''
    });

    this.modalRef.result.then(
      result => this.dismis(result),
      reason => this.dismis(reason)
    );
  }

  private openJanusModal() {
    this.janusModalOpen = true;
    this._isOpen = true;

    // Prevent body scroll when Janus modal is open
    document.body.style.overflow = 'hidden';
  }

  // Janus Modal Methods
  closeJanusModal() {
    this.janusModalOpen = false;
    this._isOpen = false;
    this.cancelProcessing();

    // Restore body scroll
    document.body.style.overflow = '';
  }

  cancelJanus() {
    this.closeJanusModal();
    this.canceled.emit();
  }

  acceptJanus() {
    if (this.closeOnAccept) {
      this.closeJanusModal();
      this.accepted.emit(this.moduleId);
    } else {
      this.setProcessing();
      this.accepted.emit();
    }
  }

  customJanus() {
    this.customButtonClicked.emit(true);
    if (this.closeOnAccept) {
      this.closeJanusModal();
    }
  }

  onJanusBackdropClick(event: MouseEvent) {
    if (!this.janusDisableBackdropClose && !this.processing) {
      this.cancelJanus();
    }
  }

  getJanusTitleClass(): string {
    return `janus-title-${this.janusModalType}`;
  }

  @HostListener('document:keydown.escape', ['$event'])
  handleEscapeKey(event: KeyboardEvent) {
    if (this.janusModalOpen && !this.processing && !this.janusDisableBackdropClose) {
      this.cancelJanus();
    }
  }

  custom() {
    this.customButtonClicked.emit(true);
  }

  isOpen() {
    return this._isOpen || this.janusModalOpen;
  }
}
